import MarkdownIt from 'markdown-it';

// CommonMark + tables + strikethrough. Raw HTML in the markdown is escaped, not
// rendered; single line breaks stay line breaks (emails and notes rely on them).
const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const FRONT_MATTER_RE = /^---\r?\n[\s\S]*?\r?\n---\r?\n/;

/**
 * Convert markdown text to HTML for rendering in TipTap editors
 */
export const markdownToHtml = (markdown: string): string => {
  return md.render(markdown.replace(FRONT_MATTER_RE, '')).trim();
};

/**
 * A markdown file as a document: the first `# Heading` becomes the title when
 * the file starts with one (and leaves the body), otherwise the filename does.
 */
export const markdownFileToDocument = (
  markdown: string,
  filename: string,
): { title: string; body: string; wordCount: number } => {
  let text = markdown.replace(FRONT_MATTER_RE, '').replace(/^\s+/, '');
  let title = filename.replace(/\.(md|markdown|mdown|mkd|mkdn)$/i, '');
  const heading = text.match(/^# +(.+?)\s*#*\s*(?:\r?\n|$)/);
  if (heading) {
    title = heading[1].trim();
    text = text.slice(heading[0].length);
  }
  const wordCount = text.replace(/[#>*_`~[\]()|-]/g, ' ').split(/\s+/).filter(Boolean).length;
  return { title, body: markdownToHtml(text), wordCount };
};

/**
 * Check if text content looks like markdown
 */
export const isMarkdownContent = (text: string): boolean => {
  // Check for common markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s/m,           // Headers
    /\*\*.*\*\*/,           // Bold
    /^\s*[-*]\s/m,          // Bullet lists
    /^\s*\d+\.\s/m,         // Numbered lists
    /`[^`]+`/,              // Inline code
    /```[\s\S]*```/,        // Code blocks
    /\[.*\]\(.*\)/,         // Links
  ];
  
  return markdownPatterns.some(pattern => pattern.test(text));
};

/**
 * Check if a filename is a markdown file
 */
export const isMarkdownFile = (filename: string): boolean => {
  const mdExtensions = ['.md', '.markdown', '.mdown', '.mkd', '.mkdn'];
  const lowerName = filename.toLowerCase();
  return mdExtensions.some(ext => lowerName.endsWith(ext));
};
